import ChatApi from '@/api/chat'
import Socket from '@/socket'
import _ from 'lodash'
import Util from '@/util'
import GaCategories from '@/constants/GaCategories'
import ahaAnalytics from '@/utils/aha-analytics/tracker'

const SET_SENDING_MESSAGE = 'SET_SENDING_MESSAGE'
const SET_SUBSCRIBING_ROOM = 'SET_SUBSCRIBING_ROOM'
const SET_ACCESS_CODE = 'SET_ACCESS_CODE'
const SET_PRESENTATION_ID = 'SET_PRESENTATION_ID'
const SET_AUDIENCE_ID = 'SET_AUDIENCE_ID'
const SET_AUDIENCE_NAME = 'SET_AUDIENCE_NAME'
const ADD_MESSAGE = 'ADD_MESSAGE'
const SET_MESSAGES = 'SET_MESSAGES'

const Events = {
  NEW_MESSAGE: 'chat:new-message',
  CLEAR_MESSAGES: 'chat:clear-messages',
}

const getStorage = () => {
  return window.localStorage
}

const getStorageKey = (accessCode) => {
  return `chat-messages-${accessCode}`
}

const getMessagesLocally = (accessCode) => {
  return JSON.parse(getStorage().getItem(getStorageKey(accessCode)) || '[]')
}

const saveMessagesLocally = ({ accessCode, messages, isThrottled }) => {
  // save messages every 10 seconds, because writing to the
  // localStorage is synchronous, and it would block the application
  // if there're lots of incoming messages
  if (isThrottled) {
    return _.throttle(() => {
      getStorage().setItem(getStorageKey(accessCode), JSON.stringify(messages))
    }, 10e3)
  }
  return () => {
    getStorage().setItem(getStorageKey(accessCode), JSON.stringify(messages))
  }
}

const deleteMessagesLocally = ({ uniqueAccessCode }) => {
  getStorage().removeItem(getStorageKey(uniqueAccessCode))
}

const convertMessage = (data) => {
  if (!data) return {}
  data.received = true
  return data
}

const getMessageIndex = (messages, msg) => {
  if (!messages || !msg) return -1
  return messages.findIndex((m) => m.timestamp === msg.timestamp)
}

const addIsSelfToMsg = (audienceId, msg) => {
  return {
    ...msg,
    isSelf: audienceId === msg.userId,
  }
}

const _state = {
  isSendingMessage: false,
  accessCode: null,
  presentationId: null,
  audienceId: null,
  audienceName: null,
  newMessageHandler: null,
  isSubscribingChatRoom: false,
  messages: [],
}

const getters = {
  isSendingMessage(state) {
    return state.isSendingMessage
  },
  isSubscribingChatRoom(state) {
    return state.isSubscribingChatRoom
  },
  chatMessages(state) {
    return state.messages
  },
}

const actions = {
  subscribeChatRoomAction: async (
    { commit, dispatch, state },
    {
      presentation,
      newMessageHandler,
      audienceId,
      audienceName,
    },
  ) => {
    if (presentation && presentation.uniqueAccessCode) {
      commit(SET_SUBSCRIBING_ROOM, true)
      commit(SET_ACCESS_CODE, presentation.uniqueAccessCode)
      commit(SET_PRESENTATION_ID, presentation.id)
      commit(SET_AUDIENCE_ID, audienceId)
      commit(SET_AUDIENCE_NAME, audienceName)
      commit(SET_MESSAGES, getMessagesLocally(presentation.uniqueAccessCode))
      Socket.getSocket().on(Events.NEW_MESSAGE, (data) => {
        const messageObj = convertMessage(data)
        if (newMessageHandler) newMessageHandler(messageObj)
        commit(ADD_MESSAGE, messageObj)
      })
      Socket.getSocket().on(Events.CLEAR_MESSAGES, () => {
        dispatch('clearChatMessagesAction', {
          uniqueAccessCode: state.accessCode,
        })
      })
    }
  },

  unsubscribeChatRoomAction: async ({ commit }, { presentation }) => {
    if (presentation && presentation.uniqueAccessCode) {
      commit(SET_SUBSCRIBING_ROOM, false)
      Socket.getSocket().off(Events.NEW_MESSAGE)
      Socket.getSocket().off(Events.CLEAR_MESSAGES)
    }
  },

  sendMessageAction: async ({ commit, state }, { payload, onSuccess, onFailure }) => {
    commit(SET_SENDING_MESSAGE, true)
    try {
      commit(ADD_MESSAGE, payload)
      const msg = await ChatApi.sendMessage(payload)
      commit(SET_SENDING_MESSAGE, false)
      Util.gaTrackEvent(
        GaCategories.AUDIENCE,
        'Chat in Lobby > Audience sends a message successfully',
        `${state.presentationId} > Chat > ${state.audienceName} > ${payload.message}`,
      )
      ahaAnalytics.trackStructEvent({
        category: 'Presentation Editor',
        action: 'Chat in Lobby > Audience sends a message',
        label: payload.presentationId,
        value: payload.message,
      })
      if (onSuccess) {
        onSuccess(msg)
      }
    } catch (error) {
      commit(SET_SENDING_MESSAGE, false)
      commit(ADD_MESSAGE, { ...payload, received: false })
      if (onFailure) {
        Util.gaTrackEvent(
          GaCategories.AUDIENCE,
          'Chat in Lobby > Audience sends a message failed',
          `${state.presentationId} > Chat > ${state.audienceName} > ${error}`,
        )
        onFailure(error)
      }
    }
  },

  clearChatMessagesAction: async ({ commit }, { uniqueAccessCode }) => {
    commit(SET_MESSAGES, [])
    deleteMessagesLocally({ uniqueAccessCode })
  },
}

const mutations = {
  [SET_SENDING_MESSAGE](state, isSendingMessage) {
    state.isSendingMessage = isSendingMessage
  },
  [SET_SUBSCRIBING_ROOM](state, isSubscribingChatRoom) {
    state.isSubscribingChatRoom = isSubscribingChatRoom
  },
  [SET_ACCESS_CODE](state, accessCode) {
    state.accessCode = accessCode
  },
  [SET_PRESENTATION_ID](state, id) {
    state.presentationId = id
  },
  [SET_AUDIENCE_ID](state, audienceId) {
    state.audienceId = audienceId
  },
  [SET_AUDIENCE_NAME](state, audienceName) {
    state.audienceName = audienceName
  },
  [ADD_MESSAGE](state, newMessage) {
    const index = getMessageIndex(state.messages, newMessage)
    const msgToAdd = addIsSelfToMsg(state.audienceId, newMessage)
    if (index === -1) {
      state.messages = [
        ...state.messages,
        msgToAdd,
      ]
      const saveMessageThrottled = saveMessagesLocally({
        accessCode: state.accessCode,
        messages: state.messages,
        throttled: true,
      })
      saveMessageThrottled()
    } else {
      const oldMsgs = [...state.messages]
      oldMsgs[index] = msgToAdd
      state.messages = oldMsgs
      const saveMessage = saveMessagesLocally({
        accessCode: state.accessCode,
        messages: state.messages,
        throttled: false,
      })
      saveMessage()
    }
  },
  [SET_MESSAGES](state, messages) {
    state.messages = messages
  },
}

export default {
  namespaced: true,
  state: _state,
  getters,
  actions,
  mutations,
}
